<script setup lang="ts">
    import { postImage } from '@/api/image.api';
    import BaseButton from '@/components/BaseButton.vue';
    import BaseIcon from '@/components/BaseIcon.vue';
    import BaseTitle from '@/components/BaseTitle.vue';
    import { acceptedImageTypes, maxImageSize } from '@/config';
    import { Events } from '@/services/Events';
    import { useConfigStore } from '@/store/config.store';
    import { useTemplateStore } from '@/store/template.store';
    import { getEmptyImageField } from '@/utils/emptyTemplate';
    import { postProductImage } from '@/api/productImage.api';
    import { getProductImageType } from '@/utils/getProductImageType';
    import { getGridUrl } from '@/utils/getGridUrl';
    import { getProduct } from '@/api/product.api';
    import { prepareCutPathField } from '@/littleWedding/CutFieldController';
    import { dataStore } from '@/commons/DataStore';
    import { storeToRefs } from 'pinia';
    import { computed, ref, useTemplateRef } from 'vue';
    import { useI18n } from 'vue-i18n';
    import type { TUploadImgType } from '@/typescript/interfaces/TUploadImgType';
    import type { TSides } from '@/typescript/interfaces/TSide';

    const { currentSide = 'recto', uploadImgType } = defineProps<{
        currentSide?: TSides;
        uploadImgType?: TUploadImgType;
    }>();
    const emit = defineEmits<{
        close: [];
    }>();

    const uploadInputRef = useTemplateRef<HTMLInputElement | null>('uploadInput');
    const { t } = useI18n();
    const { cutpathFieldList, displayGrid, isAdmin, isMobile, isProductEdition, productFormat, productId, productImages, productType } =
        storeToRefs(useConfigStore());
    const templateStore = useTemplateStore();
    const isLoading = ref(false);

    let relativeAcceptedImageTypes = acceptedImageTypes;
    if (uploadImgType === 'cutPath') {
        relativeAcceptedImageTypes = acceptedImageTypes.filter((type) => type.type === 'image/svg+xml');
    }

    const inputAccept = computed(() => {
        return relativeAcceptedImageTypes.map((type) => type.extensions).join(',');
    });

    function uploadImage() {
        isLoading.value = true;
        uploadInputRef.value?.click();
    }

    async function addImageField(file: File) {
        // classic case where we upload a field
        const newImageField = { ...getEmptyImageField(), side: currentSide ?? 'recto' };
        if (isAdmin.value) {
            newImageField.uuid = await postImage(file);
        }
        newImageField.url = URL.createObjectURL(file);
        newImageField.filename = file.name;
        templateStore.addField(newImageField);
    }

    async function saveAndSetProductImage(file: File) {
        if (!uploadImgType) {
            return;
        }
        // post image and createUrl
        await postProductImage(file, productId.value, getProductImageType(currentSide, uploadImgType));
        // set to store this product as images
        productImages.value = (await getProduct(productType.value)).images;
    }

    async function initHelpGrid() {
        // Init help grid
        const gridUrl = await getGridUrl(currentSide, productType.value, productImages.value);
        if (gridUrl) {
            const fabricWorld = dataStore.getFabricWorld(currentSide);
            await fabricWorld.createGridHelper(gridUrl, isProductEdition.value);
            fabricWorld.setGridHelperVisibility(true);
            displayGrid.value = true;
        }
    }

    function createCutPathField(file: File) {
        const newImageFieldList = prepareCutPathField(file, productFormat.value);
        cutpathFieldList.value = newImageFieldList;
    }

    async function onImageChange(e: Event) {
        const { files } = e.target as HTMLInputElement;

        if (!files?.length) {
            Events.emit('call-toaster', { title: t('error_title'), body: t('no_files'), type: 'error' });
            isLoading.value = false;
            return;
        }

        const file = files[0];
        if (!relativeAcceptedImageTypes.some((type) => type.type === file.type)) {
            let errorMessage = t('image_format_error');
            if (uploadImgType === 'cutPath') {
                errorMessage = t('image_format_error_svg');
            }
            Events.emit('call-toaster', { title: t('error_title'), body: errorMessage, type: 'error' });
            isLoading.value = false;
            return;
        }

        if (file.size > maxImageSize) {
            Events.emit('call-toaster', { title: t('error_title'), body: t('image_size_error'), type: 'error' });
            isLoading.value = false;
            return;
        }

        try {
            // Case we edit a product
            if (isProductEdition.value) {
                await saveAndSetProductImage(file);
                if (uploadImgType === 'helpGrid') {
                    await initHelpGrid();
                } else if (uploadImgType === 'cutPath') {
                    // classic case where we upload a field
                    createCutPathField(file);
                }
                emit('close');
                return;
            }
            await addImageField(file);
            emit('close');
        } catch (error) {
            Events.emit('call-toaster', { title: t('error_title'), body: error as string, type: 'error' });
        } finally {
            isLoading.value = false;
        }
    }
</script>
<template>
    <div class="fixed left-0 top-0 z-20 flex h-screen w-screen items-center justify-center bg-bg-primary/30">
        <div
            :class="[
                !isMobile && 'relative w-[362px]',
                isMobile && 'fixed bottom-0 w-full',
                'flex h-[410px] flex-col items-center gap-5 bg-bg-secondary p-6',
            ]"
        >
            <BaseTitle
                tag="h2"
                class="font-tw-cen text-base"
            >
                {{ $t('import_image') }}
            </BaseTitle>
            <button
                class="flex w-full grow cursor-pointer flex-col items-center gap-4 border border-dashed border-primary p-4 font-tw-cen leading-4"
                @click="uploadImage"
                @keydown.enter.prevent="uploadImage"
            >
                <span class="sr-only">{{ $t('upload_input_label') }}</span>
                <BaseIcon
                    type="outlined"
                    name="PhotoIcon"
                    class="h-28 w-28 text-text"
                />
                <span class="flex flex-col items-center">
                    <p class="font-bold">{{ $t('image_size_label') }}</p>
                    <p>{{ $t('image_size') }}</p>
                </span>
                <span class="flex flex-col items-center">
                    <p class="font-bold">{{ $t('allowed_formats_label') }}</p>
                    <p v-if="uploadImgType === 'cutPath'">{{ $t('allowed_formats_svg') }}</p>
                    <p v-else>{{ $t('allowed_formats') }}</p>
                </span>
            </button>
            <div class="flex h-14 w-full flex-row gap-5">
                <BaseButton
                    class="h-full w-1/2"
                    @click="$emit('close')"
                    >{{ $t('cancel') }}</BaseButton
                >
                <BaseButton
                    class="h-full w-1/2"
                    variant="filled"
                    @click="uploadImage"
                    >{{ $t('import') }}</BaseButton
                >
            </div>
            <div
                v-if="isLoading"
                class="absolute top-0 z-10 flex h-full w-full items-center justify-center bg-bg-primary/30"
            >
                <div class="h-12 w-12 animate-spin rounded-full border-4 border-t-border-primary"></div>
            </div>
        </div>
        <label
            class="sr-only"
            for="uploadInput"
        >
            {{ $t('upload_input_label') }}
        </label>
        <input
            id="uploadInput"
            ref="uploadInput"
            hidden
            type="file"
            :accept="inputAccept"
            @change="onImageChange"
            @cancel="isLoading = false"
        />
    </div>
</template>
